import { z } from 'zod'

/**
 * What a form says when a value won't do, in words a volunteer can act on:
 * "Keep it to 200 characters", not "Too big: expected string to have
 * <=200 characters". A schema's own message (`.min(1, 'Enter a first
 * name')`) still wins; this covers every check that gave none. Installed
 * once, for every schema on every platform, the server included.
 */
export function humanIssue(issue: z.core.$ZodRawIssue): string | undefined {
  const origin = 'origin' in issue ? issue.origin : undefined
  switch (issue.code) {
    case 'too_small': {
      const n = Number(issue.minimum)
      if (origin === 'string')
        return n <= 1 ? 'This can’t be empty' : `Use at least ${n} characters`
      if (origin === 'array' || origin === 'set') return `Add at least ${n}`
      if (origin === 'date') return 'Choose a later date'
      return `Use ${n} or more`
    }
    case 'too_big': {
      const n = Number(issue.maximum)
      if (origin === 'string') return `Keep it to ${n} characters`
      if (origin === 'array' || origin === 'set') return `No more than ${n}`
      if (origin === 'date') return 'Choose an earlier date'
      return `Use ${n} or less`
    }
    case 'invalid_type':
      return issue.input === undefined || issue.input === null || issue.input === ''
        ? 'This is required'
        : issue.expected === 'number'
          ? 'Enter a number'
          : 'Enter a valid value'
    case 'invalid_format':
      switch (issue.format) {
        case 'email':
          return 'Enter a valid email address'
        case 'uuid':
        case 'guid':
          return 'Choose one from the list'
        case 'date':
          return 'Enter a date'
        case 'time':
          return 'Enter a time'
        case 'datetime':
          return 'Enter a date and time'
        case 'url':
          return 'Enter a web address starting with https://'
        default:
          return 'Check the format'
      }
    case 'invalid_value':
      return issue.values.length <= 5
        ? `Choose one of: ${issue.values.map(String).join(', ')}`
        : 'Choose one from the list'
    case 'not_multiple_of':
      return `Use a multiple of ${String(issue.divisor)}`
    default:
      return undefined
  }
}

z.config({ customError: humanIssue })
